import React, { useEffect, useMemo, useRef } from 'react'

import {
  Box,
  Color,
  Display,
  FlexDirection,
  ModalProvider,
  withCitrusTheme,
  ZIndex,
} from '@gousto-internal/citrus-react'
import { useSelector } from 'react-redux'

import type { MenuResponse } from '@library/api-menu-experience'

import Loading from 'routes/Menu/Loading'
import { JustForYouTutorial } from 'routes/Menu/components/JustForYouTutorial'
import { MenuPreferencesTutorial } from 'routes/Menu/components/MenuPreferences/MenuPreferencesTutorial'
import { useShouldShowMenuPreferencesTutorial } from 'routes/Menu/components/MenuPreferences/useShouldShowMenuPreferencesTutorial'
import { useMenuBasketCta } from 'routes/Menu/context/menuBasketCtaContext'
import { useBasket } from 'routes/Menu/domains/basket'
import { CollectionSlug } from 'routes/Menu/domains/collections'
import { DeliverySettingsDropdown } from 'routes/Menu/experiments/GoustoAnytime/DeliverySettingsDropdown'
import { useResubExpDiscountsInMenu } from 'routes/Menu/hooks/useResubExpDiscountsInMenu'
import { getIsAuthenticated } from 'selectors/auth'

import type { MenuQueryParams } from '../../context/menuQueryContext'
import { BrowseScreenWrapper } from './BrowseScreenWrapper'
import { CollectionScreenWrapper } from './CollectionScreenWrapper'
import { MenuExperienceCategoryNav } from './MenuExperienceCategoryNav'
import { MENU_FIXED_BANNER_HEIGHT_VAR } from './MenuExperienceFixedBanner'
import { MenuExperienceProspectCategoryNav } from './MenuExperienceProspectCategoryNav'
import { MenuExperienceRecipeDetailOverlay } from './MenuExperienceRecipeDetailOverlay'
import { MenuExperienceSubHeader } from './MenuExperienceSubHeader'
import { ProspectBackLink } from './ProspectBackLink'
import { useMenuExperienceBasketRecipeTileSync } from './hooks/useMenuExperienceBasketRecipeTileSync'
import { useMenuExperienceNavigation } from './hooks/useMenuExperienceNavigation'
import { useMenuExperienceRecipeListTracking } from './hooks/useMenuExperienceRecipeListTracking'
import { useMenuExperienceTracking } from './hooks/useMenuExperienceTracking'
import { useProspectTempDeliverySlot } from './hooks/useProspectTempDeliverySlot'
import { MenuBrandingContext, MenuBrandingProvider, getMenuBranding } from './menuBrandingContext'
import { MenuExperienceNavigationProvider } from './menuExperienceNavigationContext'
import { MenuExperienceRecipeDetailProvider } from './menuExperienceRecipeDetailContext'
import {
  RecipeGridCustomisationProvider,
  useRecipeGridCustomisation,
} from './recipeGridCustomisationContext'

type MenuExperiencePageProps = {
  location: {
    pathname?: string
    query: MenuQueryParams & Record<string, unknown> & { collection?: string }
    [key: string]: unknown
  }
  [key: string]: unknown
}

const ContentContainer = withCitrusTheme(Box, (theme) => ({
  margin: '1rem auto 0',
  padding: '0 0 1rem',
  [`@media (min-width: ${theme.tokens.breakpoints[1]})`]: {
    padding: '0 30px 1rem',
  },
}))

const ProspectAllRecipesSpacing = withCitrusTheme(Box, (theme) => ({
  paddingTop: '18px',
  [`@media (min-width: ${theme.tokens.breakpoints[1]})`]: {
    paddingTop: '16px',
  },
  [`@media (min-width: ${theme.tokens.breakpoints[3]})`]: {
    paddingTop: '12px',
  },
}))

const MENU_EXPERIENCE_CATEGORY_NAV_STYLE = {
  position: 'sticky',
  // Stacks below the delivery settings dropdown and the fixed banner when present
  top: `calc(var(--delivery-settings-dropdown-height, 0px) + var(${MENU_FIXED_BANNER_HEIGHT_VAR}, 0px))`,
} as const

const MenuExperienceBasketRecipeTileSynchronizer = ({
  menuContent,
  menuId,
  menuUrn,
  query,
}: {
  menuContent: MenuResponse['content']
  menuId: string
  menuUrn?: MenuResponse['menuUrn']
  query: MenuExperiencePageProps['location']['query']
}) => {
  const { selectRecipeGridOption } = useRecipeGridCustomisation()

  useMenuExperienceBasketRecipeTileSync(query, menuId, menuContent, selectRecipeGridOption, menuUrn)

  return null
}

const MenuExperienceTracker = ({
  basketCount,
  browseSelectionState,
  clearBrowseSelectionState,
  data,
  deliveryDate,
}: {
  basketCount: number
  browseSelectionState: Parameters<typeof useMenuExperienceTracking>[0]['browseSelectionState']
  clearBrowseSelectionState: () => void
  data: MenuResponse
  deliveryDate?: string
}) => {
  useMenuExperienceTracking({
    basketCount,
    browseSelectionState,
    clearBrowseSelectionState,
    data,
    deliveryDate,
  })

  return null
}

const MenuExperienceRecipeListTracker = ({ data }: { data: MenuResponse }) => {
  useMenuExperienceRecipeListTracking(data)

  return null
}

export const MenuExperiencePage = ({ location }: MenuExperiencePageProps) => {
  const hasMountedRef = useRef(false)
  const lastLoadedMenuContextRef = useRef<Pick<MenuResponse, 'categoryId' | 'menuId' | 'menuUrn'>>()
  const {
    browseSelectionState,
    clearBrowseSelectionState,
    data,
    deliveryDate,
    error,
    links,
    selectCategory,
  } = useMenuExperienceNavigation(location.query, location.pathname)
  const isAuthenticated = useSelector(getIsAuthenticated)
  useProspectTempDeliverySlot()
  const { recipeCount, updateRecipePortionsByUuid } = useBasket()
  const { setLabelHTML: setBasketCtaLabelHTML } = useMenuBasketCta()
  const navigationActions = useMemo(
    () => ({ categories: data?.categories, selectCategory }),
    [data?.categories, selectCategory],
  )
  const isCategoryNavVisible = links.some((link) => link.selected)
  const browseLink = links.find(({ slug }) => slug === CollectionSlug.Browse)
  const shouldDisplayProspectBackLink = !isCategoryNavVisible && !isAuthenticated
  const recommendationsCategory = data
    ? data.categories.find(({ slug }) => slug === CollectionSlug.Recommendations)
    : undefined
  const recommendationsCollection = data
    ? recommendationsCategory
      ? { shortTitle: recommendationsCategory.title }
      : null
    : undefined
  const showMenuPreferencesTutorial =
    useShouldShowMenuPreferencesTutorial(recommendationsCollection)

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true

      return
    }

    window.scrollTo({ top: 0, behavior: 'auto' })
  }, [location.query.collection])

  useEffect(() => {
    if (data) {
      lastLoadedMenuContextRef.current = {
        categoryId: data.categoryId,
        menuId: data.menuId,
        menuUrn: data.menuUrn,
      }
    }
  }, [data])

  const fixedBanner = data?.fixedBanner
  const basketCtaLabelHTML = data?.basketCta?.labelHTML
  // Decided once for both surfaces, so a user who sees the banner and the CTA emits one exposure
  const isResubExpEnabled = useResubExpDiscountsInMenu(Boolean(fixedBanner || data?.basketCta))

  useEffect(() => {
    setBasketCtaLabelHTML(isResubExpEnabled ? basketCtaLabelHTML : undefined)

    return () => setBasketCtaLabelHTML(undefined)
  }, [isResubExpEnabled, basketCtaLabelHTML, setBasketCtaLabelHTML])

  const hasInitialLoadError = !data && error

  if (hasInitialLoadError) {
    throw error
  }

  if (!data) {
    const loading = (
      <Box data-testid="loading">
        <Loading loading />
      </Box>
    )
    const lastLoadedMenuContext = lastLoadedMenuContextRef.current

    return lastLoadedMenuContext ? (
      <>
        <DeliverySettingsDropdown />
        <RecipeGridCustomisationProvider
          key={lastLoadedMenuContext.menuId}
          collectionId={lastLoadedMenuContext.categoryId}
          menuId={lastLoadedMenuContext.menuId}
          menuUrn={lastLoadedMenuContext.menuUrn}
        >
          {loading}
        </RecipeGridCustomisationProvider>
      </>
    ) : (
      loading
    )
  }

  const ScreenWrapper = data.type === 'browse' ? BrowseScreenWrapper : CollectionScreenWrapper
  const menuBranding = getMenuBranding(data.content)
  const isProspectOnAllRecipes = !isAuthenticated && data.categorySlug === CollectionSlug.AllRecipes
  const screen = <ScreenWrapper contentBlocks={data.content} collectionId={data.categoryId} />
  const shouldGoBackToBrowse = data.type !== 'browse' && !isCategoryNavVisible
  // The full width value banner sits directly under the tabs, so it replaces the page's top margin
  const startsWithValueBanner = data.content[0]?.type === 'value-banner'

  const content = (
    <Box data-testid="menu-experience-page">
      <MenuExperienceTracker
        basketCount={recipeCount}
        browseSelectionState={browseSelectionState}
        clearBrowseSelectionState={clearBrowseSelectionState}
        data={data}
        deliveryDate={deliveryDate}
      />
      {data.type !== 'browse' && <MenuExperienceRecipeListTracker data={data} />}
      <MenuExperienceBasketRecipeTileSynchronizer
        menuContent={data.content}
        menuId={data.menuId}
        menuUrn={data.menuUrn}
        query={location.query}
      />
      {isAuthenticated && (
        <MenuExperienceSubHeader
          fixedBanner={isResubExpEnabled ? fixedBanner : undefined}
          menuId={data.menuId}
          pathname={location.pathname}
          query={location.query}
          shouldGoBackToBrowse={shouldGoBackToBrowse}
        />
      )}
      <JustForYouTutorial recommendationsCollection={recommendationsCollection} />
      {showMenuPreferencesTutorial && <MenuPreferencesTutorial />}
      {shouldDisplayProspectBackLink && browseLink && (
        <ProspectBackLink browse={browseLink} pathname={location.pathname} query={location.query} />
      )}
      {isCategoryNavVisible && (
        <Box bg={Color.White} zIndex={ZIndex.Sticky} style={MENU_EXPERIENCE_CATEGORY_NAV_STYLE}>
          {isAuthenticated ? (
            <MenuExperienceCategoryNav
              links={links}
              pathname={location.pathname}
              query={location.query}
            />
          ) : (
            <MenuExperienceProspectCategoryNav
              tabs={links}
              pathname={location.pathname}
              query={location.query}
            />
          )}
        </Box>
      )}
      <MenuBrandingProvider value={menuBranding}>
        <MenuExperienceRecipeDetailProvider
          collectionId={data.categoryId}
          collectionSlug={data.categorySlug}
          menuId={data.menuId}
          menuUrn={data.menuUrn}
          updateRecipePortions={updateRecipePortionsByUuid}
        >
          <MenuExperienceNavigationProvider value={navigationActions}>
            <MenuBrandingContext.Consumer>
              {({ backgroundColor, backgroundImageUrl, textColor }) => (
                <ContentContainer
                  data-testid="menu-experience-page-content"
                  display={Display.Flex}
                  flexDirection={FlexDirection.Column}
                  maxWidth="1600px"
                  style={{
                    marginTop:
                      backgroundColor || backgroundImageUrl || startsWithValueBanner
                        ? 0
                        : undefined,
                    ...(backgroundColor ? { backgroundColor } : {}),
                    ...(textColor ? { color: textColor } : {}),
                    ...(backgroundImageUrl
                      ? {
                          backgroundImage: `url(${backgroundImageUrl})`,
                          backgroundPosition: 'top center',
                          backgroundRepeat: 'no-repeat',
                          backgroundSize: '100% auto',
                        }
                      : {}),
                  }}
                >
                  <ModalProvider>
                    {isProspectOnAllRecipes ? (
                      <ProspectAllRecipesSpacing data-testid="prospect-all-recipes-spacing">
                        {screen}
                      </ProspectAllRecipesSpacing>
                    ) : (
                      screen
                    )}
                  </ModalProvider>
                </ContentContainer>
              )}
            </MenuBrandingContext.Consumer>
          </MenuExperienceNavigationProvider>
          <MenuExperienceRecipeDetailOverlay collectionId={data.categoryId} />
        </MenuExperienceRecipeDetailProvider>
      </MenuBrandingProvider>
    </Box>
  )

  return (
    <>
      <DeliverySettingsDropdown />
      <RecipeGridCustomisationProvider
        key={data.menuId}
        collectionId={data.categoryId}
        menuId={data.menuId}
        menuUrn={data.menuUrn}
      >
        {content}
      </RecipeGridCustomisationProvider>
    </>
  )
}
